import {useContext, useEffect, useRef, useState} from "react";
import {StoreContext} from "../../stores/StoreLoader";
import styles from './styles/WebAppInstallPrompt.module.scss';
import FontAwesome from "../utilities/FontAwesome";
import classNames from 'classnames';
import {getCookieFromString, setCookie} from "../../utils/StringUtilities";
import {useAppAllowed} from "../../hooks/useAppAllowed";
import WatsonApi from "../../backends/WatsonApi";
import NotificationManager from "../notifications/NotificationManager";
import {askPermission, isPushSupported} from "../../pages/UserSettings/NotificationSettings";
import * as React from "react";

const {UAParser} = require("ua-parser-js");

const cookieKey = "webapp";

export function WebAppInstallPrompt(props) {
    const {organizationStore, interfaceStore, modalStore, userStore} = useContext(StoreContext);

    // iOS doesn't support web app downloads, but if it's Safari we can instruct how to add to homescreen
    const isIosSafari = /(iPod|iPhone)/.exec(interfaceStore.userAgent) && /Safari/.exec(interfaceStore.userAgent);
    const isIosChrome = /(iPod|iPhone)/.exec(interfaceStore.userAgent) && /CriOS/.exec(interfaceStore.userAgent);

    const [showAlert, setShowAlert] = useState(false);
    const [showPushMessage, setShowPushMessage] = useState(false);
    const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
    const appAllowed = useAppAllowed();

    function handleInstallPrompt(e) {
        deferredPrompt.current = e;
        if (
            interfaceStore.breakpoint === "break-point-xs" &&
            !(
                window.matchMedia('(display-mode: minimal-ui)').matches ||
                window.matchMedia('(display-mode: standalone)').matches // maybe when added to homescreen on iOS?
            ) &&
            !getCookieFromString(cookieKey, document.cookie) &&
            appAllowed
        ) {
            // only show prompt on small screens
            setShowAlert(true);
        }
    }

    function handleInstalled() {
        setShowAlert(false);
    }

    function enablePush() {
        setShowPushMessage(false);

        if (isPushSupported() && userStore.user) {
            navigator.serviceWorker.ready
                .then(function (registration) {
                    askPermission()
                        .then(function () {

                            return registration.pushManager.subscribe({
                                userVisibleOnly: true,
                                applicationServerKey: "BFcZZ6FiITumPysCi_t9B1TANBHATH7r0Y4bKVRomTVRo14k5eNU0L9LhgZ3ogL7xPkGYh-RqtFN14RlnU3rDWg",
                            })

                        })
                        .then(async function (pushSubscription) {
                            console.log("Received PushSubscription: ", JSON.stringify(pushSubscription));
                            const client = await WatsonApi();
                            await client.apis.users.users_push_subscription_create({
                                user_pk: userStore.id,
                                data: {
                                    organization_id:organizationStore.currentOrganization.id,
                                    user_id: userStore.id,
                                    subscription_object: pushSubscription,
                                    user_agent: interfaceStore.userAgent,
                                    device_type: new UAParser(interfaceStore.userAgent).getDevice().type || "desktop", // https://github.com/faisalman/ua-parser-js/issues/182
                                },
                            })

                            await client.apis.organizations.organizations_users_partial_update({
                                organization_pk: organizationStore.currentOrganization.id,
                                id: (userStore.user as IUser).id,
                                data: {
                                    notifications: {
                                        phone: (userStore.user as IUser).notifications?.phone,
                                        email: (userStore.user as IUser).notifications?.email,
                                        push: true,
                                    },
                                },
                            })
                            NotificationManager.success("Settings have been updated!");
                        })
                        .catch(function (subscriptionError) {
                            NotificationManager.error(subscriptionError.message);
                        })
                })
        }
    }

    useEffect(() => {
        if (interfaceStore.breakpoint === 'break-point-xs' && !interfaceStore.isPWA && !getCookieFromString(cookieKey, document.cookie) && appAllowed) {
            setShowAlert(true);
        }

        window.addEventListener("beforeinstallprompt", handleInstallPrompt);
        window.addEventListener("appinstalled", handleInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
            window.removeEventListener("appinstalled", handleInstalled);
        }
    }, []);

    useEffect(() => {
        if (
            !isPushSupported() ||
            !navigator?.serviceWorker ||
            !("ready" in navigator.serviceWorker)
        ) {
            return;
        }

        navigator.serviceWorker.ready
            .then(() => {
                if (
                    interfaceStore.isPWA &&
                    userStore.user &&
                    !userStore.user.notifications?.push
                ) {
                    setShowPushMessage(true);
                }
            })
    }, [])

    let installButton, title;
    if (appAllowed) {
        title = organizationStore.organization.title;
        installButton = <button onClick={async () => await deferredPrompt?.current?.prompt()}>Install</button>;
    }

    const containerClassName = classNames({
        [styles.container]: true,
        [styles.containerPush]: showPushMessage && appAllowed,
        [styles.containerIosSafariPosition]: showAlert && isIosSafari && !isIosChrome && !showPushMessage,
    })

    return <div>
        {showAlert && <div className={containerClassName}>
            <div className={styles.icon}>
                <img src={organizationStore.organization.json_data.settings.webapp.icon}
                     alt={organizationStore.organization.title}/>
            </div>
            <div>
                <h2>{title}</h2>
                {isIosSafari ? <div>
                    <span>Tap the</span>
                    <svg xmlns="http://www.w3.org/2000/svg" id="Capa_1" enableBackground="new 0 0 551.13 551.13" height="20"
                         viewBox="0 0 551.13 551.13" width="40" fill={"#00bfff"} style={{verticalAlign: 'bottom'}}>
                        <path
                            d="m465.016 172.228h-51.668v34.446h34.446v310.011h-344.457v-310.011h34.446v-34.446h-51.669c-9.52 0-17.223 7.703-17.223 17.223v344.456c0 9.52 7.703 17.223 17.223 17.223h378.902c9.52 0 17.223-7.703 17.223-17.223v-344.456c0-9.52-7.703-17.223-17.223-17.223z"/>
                        <path
                            d="m258.342 65.931v244.08h34.446v-244.08l73.937 73.937 24.354-24.354-115.514-115.514-115.514 115.514 24.354 24.354z"/>
                    </svg>
                    <span>icon and then click <br/>"Add to Homescreen"</span>
                </div> :
                <div className={styles.installButton}>
                    {installButton}
                </div>
                }
            </div>
            <button onClick={() => {
                setShowAlert(false);
                // since on iOS Safari we seemingly can't determine if inside the app or not, we just want to hide it for good
                setCookie(cookieKey, "ignore", isIosSafari ? 999999 : 3);
            }}>
                <FontAwesome name={"fa-times"} size={"2x"} prefix={"fas"}/>
            </button>
        </div>}
        { (showPushMessage && appAllowed && userStore.id) &&
            <div className={containerClassName}>
                <div>
                    <h2>{title}</h2>
                    <span>Enable Push Notifications</span>
                </div>
                <div>
                    <button onClick={enablePush}>
                        Enable
                    </button>
                </div>
            </div>}
    </div>
}
